import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import clsx from 'clsx';
import { useAuthStore } from '../stores/auth';
import { useWebSocket } from '../hooks/useWebSocket';
import { api } from '../lib/api';
import LiveBidFeed from '../components/LiveBidFeed';
import BidForm from '../components/BidForm';
import CountdownTimer from '../components/CountdownTimer';
import ConnectionStatus from '../components/ConnectionStatus';
import EventLog, { DemoEvent } from '../components/EventLog';
import Icon from '../components/Icon';
import type { AuctionState, Bid } from '@auction/shared';

export default function AuctionPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('demo') === 'true';
  const { isAuthenticated, user } = useAuthStore();
  
  const [auction, setAuction] = useState<AuctionState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'warning' | 'info'; message: string } | null>(null);
  const [demoEvents, setDemoEvents] = useState<DemoEvent[]>([]);
  const [showEventLog, setShowEventLog] = useState(isDemo);

  // Helper to add demo events
  const addDemoEvent = useCallback((event: Omit<DemoEvent, 'id' | 'timestamp'>) => {
    setDemoEvents(prev => [...prev, {
      ...event,
      id: `event-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      timestamp: new Date(),
    }]);
  }, []);

  // Fetch auction data
  const fetchAuction = useCallback(async () => {
    if (!id) return;
    try {
      const response = await api.get<{ success: boolean; data: AuctionState }>(`/api/auctions/${id}`);
      setAuction(response.data);
      setLoading(false);
      
      // Add initial event when viewing in demo mode
      if (isDemo && response.data) {
        addDemoEvent({
          type: 'system',
          icon: 'play_circle',
          title: 'Demo Started',
          description: `Watching "${response.data.title}" - automated bots will compete`,
        });
      }
    } catch (err) {
      setError((err as Error).message);
      setLoading(false);
    }
  }, [id, isDemo, addDemoEvent]);

  // WebSocket connection for real-time updates
  const { isConnected } = useWebSocket({
    auctionId: id || '',
    onBid: (data) => {
      // Update auction state with new bid
      setAuction(prev => {
        if (!prev) return prev;
        const newBid: Bid = {
          id: `bid-${Date.now()}`,
          bidderId: data.bid.bidderId,
          bidderName: data.bid.bidderName,
          amount: data.bid.amount,
          timestamp: new Date(data.bid.timestamp),
          holdId: '',
        };
        return {
          ...prev,
          currentBid: newBid,
          bidHistory: [...prev.bidHistory, newBid],
          endTime: data.newEndTime ? new Date(data.newEndTime) : prev.endTime,
        };
      });

      // Log event
      addDemoEvent({
        type: 'bid',
        icon: 'gavel',
        title: `${data.bid.bidderName} placed a bid`,
        amount: data.bid.amount,
        actor: data.bid.bidderName,
      });

      // Show notification if auction was extended
      if (data.newEndTime) {
        setNotification({
          type: 'warning',
          message: 'Anti-sniping! Auction extended by 5 minutes',
        });
        addDemoEvent({
          type: 'warning',
          icon: 'timer',
          title: 'Anti-Sniping Protection Triggered',
          description: 'Auction extended by 5 minutes to allow competing bids',
        });
        setTimeout(() => setNotification(null), 5000);
      }
    },
    onOutbid: (data) => {
      setNotification({
        type: 'warning',
        message: `You've been outbid! New winning bid: £${(data.winningBid / 100).toFixed(2)}`,
      });
      addDemoEvent({
        type: 'outbid',
        icon: 'trending_up',
        title: 'Outbid Alert',
        description: `Previous leader was outbid`,
        amount: data.winningBid,
      });
      setTimeout(() => setNotification(null), 5000);
    },
    onAuctionEnded: (data) => {
      setAuction(prev => prev ? { ...prev, status: 'closed' } : prev);
      const message = data.winnerId === user?.id
        ? `Congratulations! You won for £${((data.finalPrice || 0) / 100).toFixed(2)}`
        : `Auction ended. Final price: £${((data.finalPrice || 0) / 100).toFixed(2)}`;
      setNotification({ type: 'info', message });
      
      addDemoEvent({
        type: data.winnerId ? 'success' : 'info',
        icon: data.winnerId ? 'emoji_events' : 'schedule',
        title: data.winnerId ? 'Auction Won!' : 'Auction Ended',
        description: data.winnerId 
          ? `Winner determined at final price` 
          : 'No winning bid - reserve may not have been met',
        amount: data.finalPrice || undefined,
      });
    },
    onAuctionExtended: (data) => {
      setAuction(prev => prev ? { ...prev, endTime: new Date(data.newEndTime) } : prev);
      addDemoEvent({
        type: 'warning',
        icon: 'more_time',
        title: 'Auction Extended',
        description: `New end time: ${format(new Date(data.newEndTime), 'HH:mm:ss')}`,
      });
    },
    onError: (message) => {
      console.error('WebSocket error:', message);
      addDemoEvent({
        type: 'error',
        icon: 'error',
        title: 'Connection Error',
        description: message,
      });
    },
  });

  useEffect(() => {
    fetchAuction();
  }, [fetchAuction]);

  // Place bid handler
  const handleBid = async (amount: number) => {
    if (!auction) return;
    
    await api.post(`/api/auctions/${auction.id}/bids`, { amount });
    
    addDemoEvent({
      type: 'bid',
      icon: 'person',
      title: 'You placed a bid',
      amount,
      actor: user?.displayName || 'You',
    });
    
    setNotification({
      type: 'success',
      message: `Bid of £${(amount / 100).toFixed(2)} placed successfully!`,
    });
    setTimeout(() => setNotification(null), 3000);
    
    // Refresh auction data (WebSocket will also update)
    await fetchAuction();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !auction) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Auction Not Found</h2>
        <p className="text-gray-500 mt-2">{error || 'This auction may have been removed.'}</p>
      </div>
    );
  }

  const isActive = ['active', 'ending'].includes(auction.status);
  const reversedBidHistory = [...auction.bidHistory].reverse();

  return (
    <div className="flex gap-6">
      {/* Main Auction Content */}
      <div className={clsx(
        'flex-1 transition-all duration-300',
        showEventLog ? 'max-w-4xl' : 'max-w-6xl mx-auto'
      )}>
        {/* Demo Mode Banner */}
        {isDemo && (
          <div className="mb-6 px-4 py-3 rounded-lg bg-purple-50 border border-purple-200 flex items-center gap-3">
            <Icon name="smart_toy" className="text-purple-600" />
            <div className="flex-1">
              <p className="font-medium text-purple-900">Demo Mode Active</p>
              <p className="text-sm text-purple-600">Automated bots are bidding - watch the action unfold!</p>
            </div>
            <button
              onClick={() => setShowEventLog(!showEventLog)}
              className={clsx(
                'px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors',
                showEventLog 
                  ? 'bg-purple-200 text-purple-800' 
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              )}
            >
              <Icon name="list_alt" size="small" />
              {showEventLog ? 'Hide Events' : 'Show Events'}
            </button>
          </div>
        )}

        {/* Notification Banner */}
        {notification && (
          <div className={clsx(
            'mb-6 px-4 py-3 rounded-lg flex items-center gap-3',
            notification.type === 'success' && 'bg-green-50 border border-green-200 text-green-700',
            notification.type === 'warning' && 'bg-orange-50 border border-orange-200 text-orange-700',
            notification.type === 'info' && 'bg-blue-50 border border-blue-200 text-blue-700'
          )}>
            <Icon 
              name={
                notification.type === 'success' ? 'check_circle' :
                notification.type === 'warning' ? 'warning' : 'info'
              } 
            />
            <span className="flex-1">{notification.message}</span>
            <button onClick={() => setNotification(null)}>
              <Icon name="close" size="small" />
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Image & Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image */}
            <div className="card overflow-hidden">
              <div className="aspect-video bg-gray-100 flex items-center justify-center">
                {auction.imageUrl ? (
                  <img src={auction.imageUrl} alt={auction.title} className="w-full h-full object-cover" />
                ) : (
                  <span className="material-icons-outlined text-gray-300" style={{ fontSize: '120px' }}>image</span>
                )}
              </div>
            </div>

            {/* Auction Info */}
            <div className="card p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{auction.title}</h1>
                  <p className="text-gray-500 mt-1 flex items-center gap-1">
                    <Icon name="person" size="small" />
                    Listed by {auction.sellerName}
                  </p>
                </div>
                <span className={clsx('px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1', {
                  'bg-green-100 text-green-700': auction.status === 'active',
                  'bg-orange-100 text-orange-700': auction.status === 'ending',
                  'bg-gray-100 text-gray-700': auction.status === 'closed',
                  'bg-blue-100 text-blue-700': auction.status === 'sold',
                })}>
                  <Icon name={
                    auction.status === 'active' ? 'play_circle' :
                    auction.status === 'ending' ? 'timer' :
                    auction.status === 'sold' ? 'verified' : 'stop_circle'
                  } size="small" />
                  {auction.status.toUpperCase()}
                </span>
              </div>

              {auction.description && (
                <p className="text-gray-700 mb-6">{auction.description}</p>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-3">
                  <Icon name="sell" className="text-gray-400" />
                  <div>
                    <p className="text-gray-500">Starting Price</p>
                    <p className="text-lg font-semibold">£{(auction.startingPrice / 100).toFixed(2)}</p>
                  </div>
                </div>
                {auction.reservePrice && (
                  <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-3">
                    <Icon 
                      name={auction.currentBid && auction.currentBid.amount >= auction.reservePrice 
                        ? 'check_circle' : 'cancel'} 
                      className={auction.currentBid && auction.currentBid.amount >= auction.reservePrice 
                        ? 'text-green-500' : 'text-red-400'} 
                    />
                    <div>
                      <p className="text-gray-500">Reserve Price</p>
                      <p className="text-lg font-semibold">
                        {auction.currentBid && auction.currentBid.amount >= auction.reservePrice 
                          ? 'Met' 
                          : 'Not Met'}
                      </p>
                    </div>
                  </div>
                )}
                <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-3">
                  <Icon name="event" className="text-gray-400" />
                  <div>
                    <p className="text-gray-500">Started</p>
                    <p className="font-medium">{format(new Date(auction.startTime), 'PPp')}</p>
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-3">
                  <Icon name="gavel" className="text-gray-400" />
                  <div>
                    <p className="text-gray-500">Total Bids</p>
                    <p className="text-lg font-semibold">{auction.bidHistory.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bid History */}
            <div className="card p-6">
              <LiveBidFeed 
                bids={reversedBidHistory}
                currentUserId={user?.id}
                maxItems={15}
              />
            </div>
          </div>

          {/* Right Column - Bidding Panel */}
          <div className="space-y-6">
            {/* Connection Status */}
            <div className="card p-4 flex items-center justify-between">
              <span className="text-sm text-gray-500 flex items-center gap-2">
                <Icon name="wifi" size="small" />
                Real-time updates
              </span>
              <ConnectionStatus isConnected={isConnected} />
            </div>

            {/* Current Bid Card */}
            <div className="card p-6 bg-gradient-to-br from-primary-50 to-white">
              <p className="text-sm text-gray-500 mb-1 flex items-center gap-1">
                <Icon name="payments" size="small" />
                Current Bid
              </p>
              <p className="text-4xl font-bold text-primary-600">
                £{auction.currentBid 
                  ? (auction.currentBid.amount / 100).toFixed(2) 
                  : (auction.startingPrice / 100).toFixed(2)}
              </p>
              {auction.currentBid && (
                <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
                  <Icon name="person" size="small" />
                  {auction.currentBid.bidderId === user?.id ? 'You are winning!' : `by ${auction.currentBid.bidderName}`}
                </p>
              )}
            </div>

            {/* Countdown Timer */}
            <div className="card p-6">
              <p className="text-sm text-gray-500 mb-3 text-center flex items-center justify-center gap-2">
                <Icon name="schedule" size="small" />
                {isActive ? 'Time Remaining' : 'Auction Ended'}
              </p>
              <CountdownTimer 
                endTime={auction.endTime}
                onEnd={fetchAuction}
              />
            </div>

            {/* Bid Form */}
            <div className="card p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Icon name="gavel" />
                Place Your Bid
              </h3>
              <BidForm
                currentBid={auction.currentBid?.amount || null}
                startingPrice={auction.startingPrice}
                isActive={isActive}
                onBid={handleBid}
                userId={user?.id}
                sellerId={auction.sellerId}
              />
            </div>

            {/* Your Active Bid Status */}
            {isAuthenticated && auction.currentBid?.bidderId === user?.id && auction.currentBid && (
              <div className="card p-4 bg-green-50 border-green-200">
                <div className="flex items-center gap-3">
                  <Icon name="emoji_events" className="text-yellow-500 text-3xl" />
                  <div>
                    <p className="font-semibold text-green-700">You're Winning!</p>
                    <p className="text-sm text-green-600">
                      Your bid: £{(auction.currentBid.amount / 100).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Event Log Sidebar */}
      {showEventLog && (
        <div className="w-96 flex-shrink-0 sticky top-6 self-start">
          <EventLog 
            events={demoEvents}
            maxHeight="calc(100vh - 200px)"
            title="Demo Event Log"
          />
        </div>
      )}
    </div>
  );
}
